export interface NamePrefixOptionLike {
  id: string;
  label: string;
}

/** Formats display name with optional prefix label (prefix stored separately from name). */
export function formatPersonDisplayName(
  fullName: string | null | undefined,
  prefixLabel?: string | null,
): string {
  const name = String(fullName ?? '').trim();
  const prefix = String(prefixLabel ?? '').trim();
  if (!name) {
    return prefix;
  }
  if (!prefix) {
    return name;
  }
  return `${prefix} ${name}`;
}

export function resolveNamePrefixLabel(
  namePrefixId: string | null | undefined,
  options: NamePrefixOptionLike[],
  fallbackLabel?: string | null,
): string {
  const fallback = String(fallbackLabel ?? '').trim();
  const id = String(namePrefixId ?? '').trim();
  if (!id) {
    return fallback;
  }
  const match = options.find((option) => option.id === id);
  return match?.label?.trim() || fallback;
}

export function resolveNamePrefixLabelFromRecord(
  record: Record<string, unknown>,
  identity?: Record<string, unknown>,
): string {
  const kycIdentity =
    identity ??
    ((record['kyc'] as Record<string, unknown> | undefined)?.['identity'] as
      | Record<string, unknown>
      | undefined) ??
    {};
  return String(
    record['namePrefixLabel'] ??
      record['name_prefix_label'] ??
      kycIdentity['namePrefixLabel'] ??
      kycIdentity['name_prefix_label'] ??
      '',
  ).trim();
}

export function resolveNamePrefixIdFromRecord(
  record: Record<string, unknown>,
  identity?: Record<string, unknown>,
): string | null {
  const kycIdentity =
    identity ??
    ((record['kyc'] as Record<string, unknown> | undefined)?.['identity'] as
      | Record<string, unknown>
      | undefined) ??
    {};
  const raw =
    record['namePrefixId'] ??
    record['name_prefix_value_id'] ??
    kycIdentity['namePrefixId'] ??
    kycIdentity['name_prefix_id'];
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return null;
  }
  return String(raw).trim();
}

function pickFirstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    const trimmed = String(value ?? '').trim();
    if (trimmed && trimmed !== '—' && trimmed !== '-') {
      return trimmed;
    }
  }
  return '';
}

function resolvePrefixLabelForParty(
  party: Record<string, unknown>,
  identity: Record<string, unknown>,
  prefixOptions?: NamePrefixOptionLike[],
): string {
  const fromApi = resolveNamePrefixLabelFromRecord(party, identity);
  if (fromApi) {
    return fromApi;
  }
  if (prefixOptions?.length) {
    return resolveNamePrefixLabel(
      resolveNamePrefixIdFromRecord(party, identity),
      prefixOptions,
    );
  }
  return '';
}

/** Resolves a party's display name with optional name prefix (admin parity). */
export function resolvePartyPersonDisplayName(
  party: Record<string, unknown>,
  options?: {
    fallbackNames?: unknown[];
    prefixOptions?: NamePrefixOptionLike[];
    identity?: Record<string, unknown>;
    isOwner?: boolean;
    summaryOwner?: Record<string, unknown>;
    primaryCustomer?: Record<string, unknown>;
  },
): string {
  const kyc = (party['kyc'] as Record<string, unknown> | undefined) ?? {};
  const identity = options?.identity ?? (kyc['identity'] as Record<string, unknown>) ?? {};
  const contact = (kyc['contact'] as Record<string, unknown>) ?? {};
  const partyType = String(party['partyType'] ?? 'owner')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  const isOwner = options?.isOwner ?? partyType === 'owner';
  const summaryOwner = options?.summaryOwner ?? {};
  const primaryCustomer = options?.primaryCustomer ?? {};

  const ownerFallbacks = isOwner
    ? [summaryOwner['name'], primaryCustomer['displayName'], primaryCustomer['name']]
    : [];

  const fullName = pickFirstNonEmptyString(
    party['fullName'],
    identity['fullName'],
    contact['name'],
    party['name'],
    ...ownerFallbacks,
    ...(options?.fallbackNames ?? []),
  );

  const prefixLabel = resolvePrefixLabelForParty(party, identity, options?.prefixOptions);
  const formatted = formatPersonDisplayName(fullName, prefixLabel);
  return formatted || '—';
}

/** Formats KYC full name with prefix id resolved from generic options. */
export function formatKycPersonDisplayName(
  fullName: string | null | undefined,
  namePrefixId: string | null | undefined,
  prefixOptions?: NamePrefixOptionLike[],
): string {
  const prefixLabel = prefixOptions?.length
    ? resolveNamePrefixLabel(namePrefixId, prefixOptions)
    : '';
  const formatted = formatPersonDisplayName(fullName, prefixLabel);
  return formatted || '—';
}
